/**
 * Order-ticket panel for the builder fee (#770): shown only when builder codes are on and the connected wallet has not
 * approved the fee yet. Explains what the approval is and lets the wallet sign it (one signature, no network switch).
 */
import { feeToPercent, shortAddress } from './builder';
import type { UseBuilder } from './useBuilder';
import { localizeNumber } from '../i18n/format';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

export function BuilderFeeApproval({ builder }: { builder: UseBuilder }) {
  const { cfg, approval, approving, error } = builder;
  const t = useT();
  if (!cfg || (approval !== 'needed' && approval !== 'unknown' && !error)) return null;
  const pct = localizeNumber(feeToPercent(cfg.fee));
  if (approval === 'unknown' && !error) {
    return (
      <p className="mb-2 rounded-md bg-surface-2 px-3 py-2 text-xs text-muted" data-testid="builder-approval-unknown">
        {t('builder.unknown')}
      </p>
    );
  }
  return (
    <div className="mb-2 space-y-2 rounded-md border border-accent/40 bg-accent/5 px-3 py-2 text-xs" data-testid="builder-approval">
      <p className="font-semibold text-fg">{t('builder.title', { pct })}</p>
      <p className="text-muted">
        <Msg
          k="builder.explain"
          args={{
            pct,
            address: (
              <span className="num" title={cfg.address}>
                {shortAddress(cfg.address)}
              </span>
            ),
          }}
        />
      </p>
      <p className="text-muted">{t('builder.once', { pct })}</p>
      {error && (
        <p role="alert" className="text-danger" data-testid="builder-approval-error">
          {error}
        </p>
      )}
      {approval !== 'approved' && (
        <button
          type="button"
          onClick={() => void builder.approve()}
          disabled={approving}
          className="w-full rounded-lg bg-fg px-3 py-2 text-sm font-semibold text-bg disabled:opacity-50"
          data-testid="builder-approve"
        >
          {approving ? t('builder.confirm_in_wallet') : t('builder.approve', { pct })}
        </button>
      )}
    </div>
  );
}
